import { CanvasMark } from '../plane/CanvasMark';
import { useEffect, useRef } from 'react';
import type { PointerEvent, ReactNode } from 'react';
import { useNoteText } from '../writing/useNoteText';
import type { MintedNote } from '../writing/useNoteText';
import { GripDots } from '../board/ArcRow';
import { REF_MIME, NOTE_MIME } from './dnd';
import { CloseGlyph, DockGlyph, FloatGlyph, PinGlyph } from './parts';
import { useMovable } from './useMovable';
import { NoteShell } from './HangShells';
import type { FullScreen } from './HangShells';

/**
 * Writing: one note, as one continuous text, in a panel that floats over the
 * page or docks to the right where the page gives way — in a nook, and on an
 * arc's page.
 *
 * The same note and the same editor wherever it was opened from — Write, a
 * sticky, a row. With no path there is no default note yet, and the first word
 * makes one.
 */
export function NookWriting({
  path,
  docked,
  placed,
  onPlace,
  onToggleDock,
  onClose,
  mint,
  onChange,
  label,
  onNewNote,
  onRemove,
  className,
  hung,
  swatches,
  fullScreen,
  onGripDown,
  children,
}: {
  path: string | null;
  docked: boolean;
  /** Hung off the page's Note word: the surface around it is the page's, so it has no place of its
   *  own to move to or dock in. */
  hung?: boolean;
  /** Hung off a sticky: the tint swatches that lead its head. */
  swatches?: ReactNode;
  fullScreen?: FullScreen;
  /** Draws the size grip, where the panel can be resized. */
  onGripDown?: (event: PointerEvent<HTMLElement>) => void;
  /** Where there is a plane: whether the note is on it. Absent elsewhere. */
  placed?: boolean;
  onPlace?: () => void;
  onToggleDock: () => void;
  onClose: () => void;
  mint: (text: string) => MintedNote | null;
  onChange: (path: string, raw: string) => void;
  /** A small line saying whose note this is. */
  label?: string;
  /** Starts another note, which becomes the one Write opens. */
  onNewNote?: () => void;
  /** Takes the note out of the nook that holds it; the note itself stays. */
  onRemove?: () => void;
  className?: string;
  /** Below the text: the other notes it could open instead. */
  children?: ReactNode;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  const { raw, text, missing, structured, edit } = useNoteText(path, field, { mint, onChange });
  const { offset, moving, handle } = useMovable(!docked && !hung);

  useEffect(() => {
    field.current?.focus({ preventScroll: true });
  }, [path]);

  const lead = (
    <>
      {path !== null && onPlace && (
        <span
          className="nook-writing-carry"
          title="Drag onto the canvas, or onto Write"
          // The note itself, carried out: onto the plane places it, onto
          // Write makes it the default.
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(REF_MIME, JSON.stringify({ ref: path, anchors: [] }));
            e.dataTransfer.setData(NOTE_MIME, path);
            e.dataTransfer.effectAllowed = 'copy';
          }}
        >
          <GripDots />
        </span>
      )}
      {label && <span className="nook-writing-label">{label}</span>}
    </>
  );
  const ways = (
    <>
      {onNewNote && path !== null && (
        <button type="button" className="nook-btn" onClick={onNewNote}>
          New note
        </button>
      )}
      {path !== null &&
        onPlace &&
        (placed ? (
          <span className="nook-placed">
            <CanvasMark />
          </span>
        ) : (
          <button type="button" className="nook-btn" onClick={onPlace}>
            <PinGlyph /> Add to canvas
          </button>
        ))}
      {onRemove && path !== null && (
        <button type="button" className="nook-btn" onClick={onRemove}>
          Remove from here
        </button>
      )}
    </>
  );
  const body = (
    <div className="nook-writing-body">
        {missing ? (
          <p className="nook-writing-note">This note is not in the vault.</p>
        ) : structured ? (
          <p className="nook-writing-note">
            This note holds more than one piece of content. Editing it as one run of text would
            flatten that, so it is not opened here.
          </p>
        ) : (
          <textarea
            ref={field}
            className="nook-editor"
            aria-label="Note"
            value={text}
            placeholder="Write here"
            spellCheck
            disabled={raw === null}
            onChange={(e) => edit(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
            }}
          />
        )}
    </div>
  );
  const more = children && <div className="nook-writing-more">{children}</div>;

  if (hung) {
    return (
      <NoteShell
        lead={swatches ?? lead}
        actions={<span className="nook-writing-actions">{ways}</span>}
        fullScreen={fullScreen}
        onClose={onClose}
        onGripDown={onGripDown}
      >
        {body}
        {more}
      </NoteShell>
    );
  }

  return (
    <section
      className={
        `nook-writing ${docked ? 'nook-writing-docked' : 'nook-writing-floating'}` +
        `${moving ? ' moving' : ''}${className ? ` ${className}` : ''}`
      }
      style={docked ? undefined : { transform: `translate(calc(-50% + ${offset.x}px), ${offset.y}px)` }}
      aria-label="Writing"
    >
      <div className={`nook-writing-head${docked ? '' : ' movable'}`} {...handle}>
        <div className="nook-writing-lead">{lead}</div>
        <div className="nook-writing-actions">
          {ways}
          <button
            type="button"
            className="nook-btn nook-icon"
            aria-label={docked ? 'Float over the canvas' : 'Dock to the side'}
            onClick={onToggleDock}
          >
            {docked ? <FloatGlyph /> : <DockGlyph />}
          </button>
          <button type="button" className="nook-btn nook-icon" aria-label="Close" onClick={onClose}>
            <CloseGlyph />
          </button>
        </div>
      </div>
      {body}
      {more}
    </section>
  );
}
